/**
 * Data Migration Page
 * Steg-för-steg-flöde: 1) Välj databas → 2) Välj organisationer → 3) Kör migreringar
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import {
  ArrowLeftIcon,
  CheckIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import toast from 'react-hot-toast';
import {
  migrationService,
  type DatabaseConnection,
  type SourceOrganization,
} from '@/services/migration/migrationService';
import type { MigrationPreview } from '@/types/migration';
import { MigrationOrgPicker } from './dataMigration/MigrationOrgPicker';

interface MigrationItem {
  id: string;
  name: string;
  description: string;
  order: number;
  selected: boolean;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  result?: {
    recordsProcessed: number;
    recordsSucceeded: number;
    recordsFailed: number;
    errors: string[];
    warnings: string[];
    sso?: boolean;
  };
}

const STEPS = [1, 2, 3] as const;

const DataMigration = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Step 1 state
  const [selectedDatabase, setSelectedDatabase] = useState<string>('');
  const [databases, setDatabases] = useState<DatabaseConnection[]>([]);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  // Step 2 state
  const [sourceOrganizations, setSourceOrganizations] = useState<SourceOrganization[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set());
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Step 3 state
  const [migrations, setMigrations] = useState<MigrationItem[]>([]);
  const [runningMigration, setRunningMigration] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [preview, setPreview] = useState<MigrationPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadDatabases();
  }, []);

  const initMigrations = () => {
    setMigrations([
      {
        id: '001-organizations',
        name: t('migration.organizations'),
        description: t('migration.organizationsDescription'),
        order: 1,
        selected: false,
        status: 'pending',
      },
      {
        id: '002-users',
        name: t('migration.users'),
        description: t('migration.usersDescription'),
        order: 2,
        selected: false,
        status: 'pending',
      },
      {
        id: '003-vehicles',
        name: t('migration.vehicles'),
        description: t('migration.vehiclesDescription'),
        order: 3,
        selected: false,
        status: 'pending',
      },
      {
        id: '004-device-vehicle-history',
        name: t('migration.deviceVehicleHistory'),
        description: t('migration.deviceVehicleHistoryDescription'),
        order: 4,
        selected: false,
        status: 'pending',
      },
      {
        id: '005-device-organizations',
        name: t('migration.deviceOrganizations'),
        description: t('migration.deviceOrganizationsDescription'),
        order: 5,
        selected: false,
        status: 'pending',
      },
      {
        id: '006-mongo-trips-vehicle-backfill',
        name: t('migration.mongoTripsVehicleBackfill'),
        description: t('migration.mongoTripsVehicleBackfillDescription'),
        order: 6,
        selected: false,
        status: 'pending',
      },
      {
        id: '007-booking-places',
        name: t('migration.bookingPlaces'),
        description: t('migration.bookingPlacesDescription'),
        order: 7,
        selected: false,
        status: 'pending',
      },
      {
        id: '008-booking-users',
        name: t('migration.bookingUsers'),
        description: t('migration.bookingUsersDescription'),
        order: 8,
        selected: false,
        status: 'pending',
      },
      {
        id: '009-booking-bookings',
        name: t('migration.bookingBookings'),
        description: t('migration.bookingBookingsDescription'),
        order: 9,
        selected: false,
        status: 'pending',
      },
    ]);
  };

  const loadDatabases = async () => {
    try {
      const dbList = await migrationService.getDatabases();
      setDatabases(dbList);
    } catch (error: any) {
      toast.error(t('migration.errorLoadingDatabases') + ': ' + error.message);
    }
  };

  const testConnection = async (dbId: string) => {
    try {
      setTestingConnection(dbId);
      setDatabases(prev =>
        prev.map(db =>
          db.id === dbId ? { ...db, status: 'testing' as const } : db
        )
      );
      const result = await migrationService.testConnection(dbId);
      setDatabases(prev =>
        prev.map(db =>
          db.id === dbId
            ? { ...db, status: result.connected ? ('connected' as const) : ('disconnected' as const) }
            : db
        )
      );
      if (result.connected) {
        toast.success(t('migration.connectionSuccess'));
      } else {
        toast.error(t('migration.connectionFailed') + ': ' + result.error);
      }
    } catch (error: any) {
      setDatabases(prev =>
        prev.map(db =>
          db.id === dbId ? { ...db, status: 'disconnected' as const } : db
        )
      );
      toast.error(t('migration.connectionError') + ': ' + error.message);
    } finally {
      setTestingConnection(null);
    }
  };

  const loadSourceOrganizations = async () => {
    if (!selectedDatabase) return;
    setLoadingOrgs(true);
    try {
      const orgs = await migrationService.getSourceOrganizations(selectedDatabase);
      setSourceOrganizations(orgs);
      setSelectedOrgIds(new Set());
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const toggleOrgSelection = (orgId: number) => {
    setSelectedOrgIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const goToStep2 = async () => {
    if (!selectedDatabase) {
      toast.error(t('migration.selectDatabaseFirst'));
      return;
    }
    await loadSourceOrganizations();
    setCurrentStep(2);
  };

  const goToStep3 = () => {
    if (selectedOrgIds.size === 0) {
      toast.error(t('migration.selectOrganizationsFirst'));
      return;
    }
    initMigrations();
    setPreview(null);
    setCurrentStep(3);
  };

  const toggleMigration = (migrationId: string) => {
    if (runningMigration !== null) return;
    setMigrations(prev =>
      prev.map(m =>
        m.id === migrationId ? { ...m, selected: !m.selected } : m
      )
    );
  };

  const runMigration = async (migrationId: string) => {
    try {
      setRunningMigration(migrationId);
      setMigrations(prev =>
        prev.map(m =>
          m.id === migrationId ? { ...m, status: 'running' as const } : m
        )
      );

      const result = await migrationService.runMigration(
        selectedDatabase,
        migrationId,
        dryRun,
        Array.from(selectedOrgIds)
      );

      setMigrations(prev =>
        prev.map(m =>
          m.id === migrationId
            ? {
                ...m,
                status: result.success ? ('completed' as const) : ('failed' as const),
                result: {
                  recordsProcessed: result.recordsProcessed,
                  recordsSucceeded: result.recordsSucceeded,
                  recordsFailed: result.recordsFailed,
                  errors: result.errors,
                  warnings: result.warnings,
                  sso: result.sso,
                },
              }
            : m
        )
      );

      if (result.success) {
        toast.success(
          t('migration.success', {
            processed: result.recordsProcessed,
            succeeded: result.recordsSucceeded,
          })
        );
      } else {
        toast.error(
          t('migration.failed', {
            processed: result.recordsProcessed,
            failed: result.recordsFailed,
          })
        );
      }
    } catch (error: any) {
      setMigrations(prev =>
        prev.map(m =>
          m.id === migrationId ? { ...m, status: 'failed' as const } : m
        )
      );
      toast.error(t('migration.error') + ': ' + error.message);
    } finally {
      setRunningMigration(null);
    }
  };

  const runSelectedMigrations = async () => {
    const selected = migrations.filter(m => m.selected).sort((a, b) => a.order - b.order);
    if (selected.length === 0) {
      toast.error(t('migration.selectMigrationsFirst'));
      return;
    }
    for (const migration of selected) {
      await runMigration(migration.id);
    }
  };

  const runPreview = async () => {
    if (!selectedDatabase || selectedOrgIds.size === 0) return;
    setLoadingPreview(true);
    try {
      const result = await migrationService.preview(
        selectedDatabase,
        Array.from(selectedOrgIds)
      );
      setPreview(result);
    } catch (error: any) {
      toast.error(t('migration.previewError') + ': ' + error.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  // --- Render helpers ---

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, idx) => {
        const isActive = currentStep === step;
        const isCompleted = currentStep > step;
        const labels = [t('migration.step1'), t('migration.step2'), t('migration.step3')];
        return (
          <div key={step} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-bgPrimary text-textLight'
                    : isCompleted
                    ? 'bg-green-500 text-white'
                    : 'bg-bgLight text-textTertiary'
                }`}
              >
                {isCompleted ? <CheckIcon className="w-4 h-4" /> : step}
              </div>
              <span
                className={`text-sm font-medium ${
                  isActive ? 'text-textDefault' : 'text-textTertiary'
                }`}
              >
                {labels[idx]}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className="w-12 h-px bg-border mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <div>
      <h2 className="text-lg font-semibold text-textDefault mb-4">
        {t('migration.selectDatabase')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {databases.map(db => (
          <div
            key={db.id}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              selectedDatabase === db.id
                ? 'border-bgPrimary bg-BgPrimaryLight'
                : 'border-border bg-bgContainer hover:border-bgPrimary'
            }`}
            onClick={() => setSelectedDatabase(db.id)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-textDefault">{db.name}</h3>
              <button
                onClick={e => {
                  e.stopPropagation();
                  testConnection(db.id);
                }}
                disabled={testingConnection === db.id}
                className={`px-2 py-1 text-xs rounded ${
                  testingConnection === db.id
                    ? 'bg-bgLight text-textTertiary cursor-not-allowed'
                    : 'bg-bgPrimary text-textLight hover:opacity-90'
                }`}
              >
                {testingConnection === db.id
                  ? t('migration.testing')
                  : t('migration.testConnection')}
              </button>
            </div>
            <p className="text-xs text-textSecondary mb-1">
              {db.server}:{db.port}
            </p>
            <p className="text-xs text-textTertiary">{db.database}</p>
            {db.status === 'connected' && (
              <div className="mt-2 text-xs text-green-600">
                &#10003; {t('migration.connected')}
              </div>
            )}
            {db.status === 'disconnected' && (
              <div className="mt-2 text-xs text-red-600">
                &#10007; {t('migration.disconnected')}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button
          onClick={goToStep2}
          disabled={!selectedDatabase}
          className={`px-6 py-2 rounded-lg font-medium transition-opacity ${
            !selectedDatabase
              ? 'bg-bgLight text-textTertiary cursor-not-allowed'
              : 'bg-bgPrimary text-textLight hover:opacity-90'
          }`}
        >
          {t('migration.next')}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h2 className="text-lg font-semibold text-textDefault mb-1">
        {t('migration.selectOrganizations')}
      </h2>
      <p className="text-sm text-textTertiary mb-4">
        {t('migration.selectOrganizationsDescription')}
      </p>

      {loadingOrgs ? (
        <div className="flex items-center justify-center py-16 text-textSecondary">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-bgPrimary mr-3" />
          {t('migration.loadingOrganizations')}
        </div>
      ) : sourceOrganizations.length === 0 ? (
        <div className="py-16 text-center text-textTertiary">
          {t('migration.noOrganizationsFound')}
        </div>
      ) : (
        <MigrationOrgPicker
          organizations={sourceOrganizations}
          selectedIds={selectedOrgIds}
          onToggle={toggleOrgSelection}
          onReplaceSelection={setSelectedOrgIds}
        />
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-6 py-2 rounded-lg font-medium border border-border bg-bgElevated text-textDefault hover:bg-bgLight"
        >
          {t('migration.back')}
        </button>
        <button
          onClick={goToStep3}
          disabled={selectedOrgIds.size === 0}
          className={`px-6 py-2 rounded-lg font-medium transition-opacity ${
            selectedOrgIds.size === 0
              ? 'bg-bgLight text-textTertiary cursor-not-allowed'
              : 'bg-bgPrimary text-textLight hover:opacity-90'
          }`}
        >
          {t('migration.next')} ({selectedOrgIds.size})
        </button>
      </div>
    </div>
  );

  const groupStatusBadge = (status: 'mapped' | 'name_collision' | 'new') => {
    if (status === 'mapped') {
      return <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">{t('migration.previewGroupExists')}</span>;
    }
    if (status === 'name_collision') {
      return <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">{t('migration.previewGroupNameCollision')}</span>;
    }
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">{t('migration.previewGroupNew')}</span>;
  };

  const renderPreviewPanel = () => {
    const vehiclesDifferent = preview
      ? preview.vehicleChecks.filter(v => v.status === 'different_group')
      : [];
    const vehiclesSameReg = preview
      ? preview.vehicleChecks.filter(v => v.status === 'same_registration')
      : [];
    const vehiclesSkipped = preview
      ? preview.vehicleChecks.filter(v => v.status === 'unmapped' || v.status === 'no_imei')
      : [];
    return (
      <div className="mb-6 p-4 bg-bgElevated rounded-lg border border-border">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-medium text-textDefault">{t('migration.previewTitle')}</h3>
            <p className="text-xs text-textTertiary mt-1">{t('migration.previewDescription')}</p>
          </div>
          <button
            onClick={runPreview}
            disabled={loadingPreview || runningMigration !== null}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-opacity ${
              loadingPreview || runningMigration !== null
                ? 'bg-bgLight text-textTertiary cursor-not-allowed'
                : 'bg-bgPrimary text-textLight hover:opacity-90'
            }`}
          >
            {loadingPreview ? t('migration.previewing') : t('migration.preview')}
          </button>
        </div>

        {preview && (
          <div className="mt-4 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <div className="p-2 rounded bg-bgContainer border border-border">
                <div className="text-textTertiary text-xs">{t('migration.previewGroups')}</div>
                <div className="font-semibold text-textDefault">
                  {preview.summary.orgsExisting}/{preview.summary.orgsTotal}
                  {preview.summary.orgsNew > 0 && (
                    <span className="text-xs text-blue-600 ml-1">(+{preview.summary.orgsNew} {t('migration.previewNewShort')})</span>
                  )}
                </div>
              </div>
              <div className={`p-2 rounded border ${preview.summary.conflictCount > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800' : 'bg-bgContainer border-border'}`}>
                <div className="text-textTertiary text-xs">{t('migration.previewConflicts')}</div>
                <div className="font-semibold text-textDefault">{preview.summary.conflictCount}</div>
              </div>
              <div className={`p-2 rounded border ${preview.summary.vehiclesDifferentGroup > 0 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800' : 'bg-bgContainer border-border'}`}>
                <div className="text-textTertiary text-xs">{t('migration.previewVehiclesOtherGroup')}</div>
                <div className="font-semibold text-textDefault">{preview.summary.vehiclesDifferentGroup}</div>
              </div>
              <div className={`p-2 rounded border ${(preview.summary.vehiclesSameRegistration ?? 0) > 0 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800' : 'bg-bgContainer border-border'}`}>
                <div className="text-textTertiary text-xs">{t('migration.previewVehiclesSameRegistration')}</div>
                <div className="font-semibold text-textDefault">{preview.summary.vehiclesSameRegistration ?? 0}</div>
              </div>
              <div className="p-2 rounded bg-bgContainer border border-border">
                <div className="text-textTertiary text-xs">{t('migration.previewVehicles')}</div>
                <div className="font-semibold text-textDefault">
                  {preview.summary.vehiclesOk}/{preview.summary.vehiclesTotal}
                  {(preview.summary.vehiclesUnmapped > 0 || preview.summary.vehiclesNoImei > 0) && (
                    <span className="text-xs text-red-600 ml-1">
                      (-{preview.summary.vehiclesUnmapped + preview.summary.vehiclesNoImei})
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Group overview (all folders, status mapped/new/collision) */}
            <div>
              <h4 className="text-sm font-medium text-textDefault mb-2">
                {t('migration.previewGroupOverview')} ({preview.organizationChecks.length})
              </h4>
              <div className="max-h-56 overflow-y-auto border border-border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-bgContainer text-textTertiary sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">{t('migration.previewColName')}</th>
                      <th className="text-left px-2 py-1">{t('migration.previewColStatus')}</th>
                      <th className="text-left px-2 py-1">{t('migration.previewColInSystem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.organizationChecks.map(g => (
                      <tr key={`grp-${g.o_id}`} className="border-t border-border">
                        <td className="px-2 py-1 text-textDefault">{g.o_name}</td>
                        <td className="px-2 py-1">{groupStatusBadge(g.status)}</td>
                        <td className="px-2 py-1 text-textSecondary">
                          {g.status === 'mapped'
                            ? `#${g.targetOrgId}${g.targetTopNodeName ? ' (' + g.targetTopNodeName + ')' : ''}`
                            : g.status === 'name_collision'
                            ? g.existing.map(e => `#${e.id}${e.topNodeName ? ' (' + e.topNodeName + ')' : ''}`).join(', ')
                            : t('migration.previewGroupWillCreate')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-textTertiary mt-1">{t('migration.previewGroupNote')}</p>
            </div>

            {/* Structure conflicts (rehome) */}
            {preview.structureConflicts.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  {t('migration.previewConflicts')} ({preview.structureConflicts.length})
                </h4>
                <div className="max-h-48 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bgContainer text-textTertiary">
                      <tr>
                        <th className="text-left px-2 py-1">{t('migration.previewColName')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColCurrentParent')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColSourceParent')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.structureConflicts.map(c => (
                        <tr key={`conf-${c.o_id}`} className="border-t border-border">
                          <td className="px-2 py-1 text-textDefault">{c.o_name}</td>
                          <td className="px-2 py-1 text-textSecondary">{c.currentParentName || '-'}</td>
                          <td className="px-2 py-1 text-textSecondary">{c.sourceParentName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Vehicles that already exist in another group */}
            {vehiclesDifferent.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-400 mb-2">
                  {t('migration.previewVehiclesOtherGroup')} ({vehiclesDifferent.length})
                </h4>
                <div className="max-h-56 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bgContainer text-textTertiary sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">{t('migration.previewColReg')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColImei')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColCurrentGroup')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColTargetGroup')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehiclesDifferent.map(v => (
                        <tr key={`veh-diff-${v.c_id}`} className="border-t border-border">
                          <td className="px-2 py-1 text-textDefault">{v.registration || '-'}</td>
                          <td className="px-2 py-1 text-textTertiary font-mono">{v.imei || '-'}</td>
                          <td className="px-2 py-1 text-yellow-700 dark:text-yellow-300">
                            {v.currentOrgName || '-'}
                            {v.currentTopNodeName ? ` (${v.currentTopNodeName})` : ''}
                          </td>
                          <td className="px-2 py-1 text-textSecondary">
                            {v.mappedOrgName || '-'}
                            {v.topNodeName ? ` (${v.topNodeName})` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-textTertiary mt-1">{t('migration.previewVehiclesOtherGroupNote')}</p>
              </div>
            )}

            {vehiclesSameReg.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-2">
                  {t('migration.previewVehiclesSameRegistration')} ({vehiclesSameReg.length})
                </h4>
                <div className="max-h-56 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bgContainer text-textTertiary sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">{t('migration.previewColReg')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColImei')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColExistingVehicle')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColExistingImei')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColExtraRows')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehiclesSameReg.map(v => (
                        <tr key={`veh-reg-${v.c_id}`} className="border-t border-border">
                          <td className="px-2 py-1 text-textDefault">{v.registration || '-'}</td>
                          <td className="px-2 py-1 text-textTertiary font-mono">{v.imei || '-'}</td>
                          <td className="px-2 py-1 text-textSecondary">
                            {v.sameRegistrationVehicleId != null ? `#${v.sameRegistrationVehicleId}` : '-'}
                            {v.currentOrgName ? ` (${v.currentOrgName})` : ''}
                          </td>
                          <td className="px-2 py-1 text-textTertiary font-mono">{v.sameRegistrationImei || '-'}</td>
                          <td className="px-2 py-1 text-textSecondary">{v.extraRegistrationCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-textTertiary mt-1">{t('migration.previewVehiclesSameRegistrationNote')}</p>
              </div>
            )}

            {/* Vehicles skipped */}
            {vehiclesSkipped.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  {t('migration.previewVehicleIssues')} ({vehiclesSkipped.length})
                </h4>
                <div className="max-h-48 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bgContainer text-textTertiary">
                      <tr>
                        <th className="text-left px-2 py-1">{t('migration.previewColReg')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColImei')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColPath')}</th>
                        <th className="text-left px-2 py-1">{t('migration.previewColStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehiclesSkipped.map(v => (
                        <tr key={`veh-skip-${v.c_id}`} className="border-t border-border">
                          <td className="px-2 py-1 text-textDefault">{v.registration || '-'}</td>
                          <td className="px-2 py-1 text-textTertiary font-mono">{v.imei || '-'}</td>
                          <td className="px-2 py-1 text-textTertiary font-mono">{v.o_path}</td>
                          <td className="px-2 py-1">
                            <span className="text-red-600">
                              {v.status === 'unmapped'
                                ? t('migration.previewStatusUnmapped')
                                : t('migration.previewStatusNoImei')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.summary.orgsNew === 0 &&
              preview.structureConflicts.length === 0 &&
              vehiclesDifferent.length === 0 &&
              vehiclesSameReg.length === 0 &&
              vehiclesSkipped.length === 0 && (
                <div className="text-sm text-green-600">{t('migration.previewNoIssues')}</div>
              )}
          </div>
        )}
      </div>
    );
  };

  const renderStep3 = () => (
    <div>
      {/* Dry Run Toggle */}
      <div className="mb-6 p-4 bg-bgElevated rounded-lg border border-border">
        <label className="flex items-center gap-2 text-textDefault">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            className="w-4 h-4 border border-border rounded bg-bgElevated"
          />
          <span className="font-medium">{t('migration.dryRun')}</span>
        </label>
        <p className="text-xs text-textTertiary mt-2 ml-6">
          {t('migration.dryRunDescription')}
        </p>
      </div>

      {/* Summary */}
      <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
          {databases.find(d => d.id === selectedDatabase)?.name} &mdash;{' '}
          {t('migration.selectedCount', { count: selectedOrgIds.size })}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {sourceOrganizations
            .filter(org => selectedOrgIds.has(org.o_id))
            .map(org => (
              <span
                key={org.o_id}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200"
              >
                {org.o_name}
              </span>
            ))}
        </div>
      </div>

      {/* Preview / Validation */}
      {renderPreviewPanel()}

      {/* Migrations List */}
      <h2 className="text-lg font-semibold text-textDefault mb-2">
        {t('migration.selectMigrations')}
      </h2>
      <p className="text-sm text-textTertiary mb-4">{t('migration.migrationOrder')}</p>

      <div className="space-y-3 mb-6">
        {migrations
          .sort((a, b) => a.order - b.order)
          .map(migration => (
            <div
              key={migration.id}
              className="p-4 bg-bgElevated rounded-lg border border-border"
            >
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  checked={migration.selected}
                  onChange={() => toggleMigration(migration.id)}
                  disabled={runningMigration !== null}
                  className="mt-1 w-4 h-4 border border-border rounded bg-bgElevated cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={e => e.stopPropagation()}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-textTertiary">
                      #{migration.order}
                    </span>
                    <h3 className="text-lg font-semibold text-textDefault">
                      {migration.name}
                    </h3>
                  </div>
                  <p className="text-sm text-textSecondary mb-2">
                    {migration.description}
                  </p>
                  {migration.status === 'running' && (
                    <div className="text-sm text-textPrimary">
                      {t('migration.running')}...
                    </div>
                  )}
                  {migration.status === 'completed' && migration.result && (
                    <div className="text-sm text-green-600">
                      {t('migration.completed')}: {migration.result.recordsSucceeded}/
                      {migration.result.recordsProcessed} {t('migration.records')}
                    </div>
                  )}
                  {migration.status === 'failed' && migration.result && (
                    <div className="text-sm text-red-600">
                      {t('migration.failed')}: {migration.result.recordsFailed}/
                      {migration.result.recordsProcessed} {t('migration.records')}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => runMigration(migration.id)}
                  disabled={
                    runningMigration !== null || !migration.selected
                  }
                  className={`px-4 py-2 rounded-lg transition-opacity ${
                    runningMigration !== null || !migration.selected
                      ? 'bg-bgLight text-textTertiary cursor-not-allowed'
                      : 'bg-bgPrimary text-textLight hover:opacity-90'
                  }`}
                >
                  {migration.status === 'running'
                    ? t('migration.running')
                    : migration.status === 'completed'
                    ? t('migration.rerun')
                    : t('migration.run')}
                </button>
              </div>

              {/* Result Details */}
              {migration.result && (
                <div className="mt-4 pt-4 border-t border-border ml-8">
                  <div className={`grid ${typeof migration.result.sso === 'boolean' ? 'grid-cols-4' : 'grid-cols-3'} gap-4 text-sm mb-4`}>
                    <div>
                      <span className="text-textTertiary">
                        {t('migration.processed')}:
                      </span>
                      <span className="ml-2 font-medium text-textDefault">
                        {migration.result.recordsProcessed}
                      </span>
                    </div>
                    <div>
                      <span className="text-textTertiary">
                        {t('migration.succeeded')}:
                      </span>
                      <span className="ml-2 font-medium text-green-600">
                        {migration.result.recordsSucceeded}
                      </span>
                    </div>
                    <div>
                      <span className="text-textTertiary">
                        {t('migration.failed')}:
                      </span>
                      <span className="ml-2 font-medium text-red-600">
                        {migration.result.recordsFailed}
                      </span>
                    </div>
                    {typeof migration.result.sso === 'boolean' && (
                      <div>
                        <span className="text-textTertiary">{t('migration.sso')}:</span>
                        <span className="ml-2 font-medium text-textDefault">
                          {migration.result.sso ? '✓' : '—'}
                        </span>
                      </div>
                    )}
                  </div>

                  {migration.result.errors.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-red-600 mb-2">
                        {t('migration.errors')} ({migration.result.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto bg-red-50 dark:bg-red-900/20 rounded p-2">
                        {migration.result.errors.slice(0, 5).map((error, idx) => (
                          <div key={idx} className="text-xs text-red-600 mb-1">
                            {error}
                          </div>
                        ))}
                        {migration.result.errors.length > 5 && (
                          <div className="text-xs text-red-600">
                            ... {migration.result.errors.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {migration.result.warnings.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-yellow-600 mb-2">
                        {t('migration.warnings')} ({migration.result.warnings.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto bg-yellow-50 dark:bg-yellow-900/20 rounded p-2">
                        {migration.result.warnings.slice(0, 5).map((warning, idx) => (
                          <div key={idx} className="text-xs text-yellow-600 mb-1">
                            {warning}
                          </div>
                        ))}
                        {migration.result.warnings.length > 5 && (
                          <div className="text-xs text-yellow-600">
                            ... {migration.result.warnings.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Bottom actions */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(2)}
          disabled={runningMigration !== null}
          className={`px-6 py-2 rounded-lg font-medium border border-border bg-bgElevated text-textDefault ${
            runningMigration !== null ? 'opacity-50 cursor-not-allowed' : 'hover:bg-bgLight'
          }`}
        >
          {t('migration.back')}
        </button>
        <button
          onClick={runSelectedMigrations}
          disabled={
            runningMigration !== null ||
            migrations.filter(m => m.selected).length === 0
          }
          className={`px-6 py-3 rounded-lg font-medium transition-opacity ${
            runningMigration !== null ||
            migrations.filter(m => m.selected).length === 0
              ? 'bg-bgLight text-textTertiary cursor-not-allowed'
              : 'bg-bgPrimary text-textLight hover:opacity-90'
          }`}
        >
          {t('migration.runSelected')} ({migrations.filter(m => m.selected).length})
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={ROUTES.SYSADMIN}
          className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ArrowsRightLeftIcon className="w-6 h-6 text-primary" />
            {t('migration.title')}
          </h1>
          <p className="text-sm text-textSecondary mt-1">
            {t('migration.description')}
          </p>
        </div>
      </div>

      {renderStepIndicator()}

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </div>
  );
};

export default DataMigration;
